import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Megaphone,
  Calendar,
  Clock,
  Search,
  Filter,
  Eye,
  Star,
  ChevronRight,
  Bell,
  AlertTriangle,
  Info,
  CheckCircle
} from 'lucide-react';

import { apiFetch } from "../utils/api";
import { useAuth } from '../Services/AuthContext';

export function Announcements({ initialAnnouncementId }) {

  const { login, user, token, idToken, currentPartner, setCurrentPartner, appUser } = useAuth();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<any>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [announcements, setAnnouncements] = useState([]);
  const [filteredAnnouncements, setFilteredAnnouncements] = useState(announcements);

  const fetchAnnouncements = async () => {
    let tk = await idToken();
    const res = apiFetch(tk, `/api/announcements`);
    res.then(async (response) => {
      setAnnouncements(response.data);
    });
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  // Handle initial announcement selection from navigation
  useEffect(() => {
    if (initialAnnouncementId && announcements.length > 0) {
      const announcement = announcements.find(a => a.id === initialAnnouncementId);
      if (announcement) {
        setSelectedAnnouncement(announcement);
      }
    }
  }, [initialAnnouncementId, announcements]);

  useEffect(() => {
    const f = announcements.filter(announcement => {
      const matchesSearch = announcement.announcement.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        announcement.announcement.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        announcement.announcement.tags.split(',').some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesCategory = selectedCategory === 'all' ||
        announcement.announcement.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });
    setFilteredAnnouncements(f);    
  }, [announcements, searchTerm, selectedCategory]);


  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-worldsview-red';
      case 'medium': return 'bg-worldsview-orange';
      case 'low': return 'bg-worldsview-blue';
      default: return 'bg-gray-500';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high': return AlertTriangle;
      case 'medium': return Info;
      case 'low': return CheckCircle;
      default: return Info;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'pricing': return 'bg-worldsview-purple';
      case 'training': return 'bg-worldsview-green';
      case 'events': return 'bg-worldsview-blue';
      case 'benefits': return 'bg-worldsview-orange';
      case 'security': return 'bg-worldsview-red';
      default: return 'bg-gray-500';
    }
  };

  if (selectedAnnouncement) {
    return (
      
      <div className="space-y-6 animate-fade-in-fast">
        {/* Back Button */}
        
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => setSelectedAnnouncement(null)}
            className="glass-button"
          >
            ← Back to Announcements
          </Button>
        </div>

        {/* Announcement Detail */}
        <Card className="glass-card">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <Badge className={`text-white ${getPriorityColor(selectedAnnouncement.announcement.priority)}`}>
                    {selectedAnnouncement.announcement.priority.toUpperCase()}
                  </Badge>
                  <Badge variant="outline" className={`text-white ${getCategoryColor(selectedAnnouncement.announcement.category)}`}>
                    {selectedAnnouncement.announcement.category.toUpperCase()}
                  </Badge>
                  {!selectedAnnouncement.read && (
                    <Badge className="bg-worldsview-blue text-white">UNREAD</Badge>
                  )}
                </div>

                <h1 className="mb-4">{selectedAnnouncement.announcement.title}</h1>

                <div className="flex items-center gap-6 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {selectedAnnouncement.announcement.publish_date}
                  </div>
                  <div>By {selectedAnnouncement.announcement.author}</div>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="prose prose-sm max-w-none space-y-4">
              <p className="text-base leading-relaxed whitespace-pre-wrap">{selectedAnnouncement.announcement.content}</p>

              
            </div>

            <div className="flex flex-wrap gap-2">
              {selectedAnnouncement.announcement.tags.split(',').map((tag: string) => (
                <Badge key={tag} variant="outline" className="glass-badge">
                  {tag}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-fast">
      {/* Header */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1>Announcements</h1>
            <p className="text-muted-foreground mt-2">
              Stay up to date with the latest news and updates from Worldsview
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-worldsview-blue" />
            <Badge className="bg-worldsview-red text-white">
              {announcements.filter(a => !a.read).length} Unread
            </Badge>
          </div>
        </div>
      </div>



      {/* Search and Filters */}
      <Card className="glass-card">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search announcements..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 glass-input"
              />
            </div>
            <div className="flex gap-2">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-48 glass-input">
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="pricing">Pricing</SelectItem>
                  <SelectItem value="training">Training</SelectItem>
                  <SelectItem value="events">Events</SelectItem>
                  <SelectItem value="benefits">Benefits</SelectItem>
                  <SelectItem value="security">Security</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                className="glass-button"
                onClick={() => {
                  setSelectedCategory('all');
                  setSearchTerm('');
                }}
              >
                <Filter className="w-4 h-4 mr-2" />
                Clear Filters
              </Button>
            </div>
          </div>

          {/* Active Filter Display */}
          {selectedCategory !== 'all' && (
            <div className="mt-4 flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Filtering by:</span>
              <Badge variant="outline" className="glass-badge">
                {selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)}
                <button
                  onClick={() => setSelectedCategory('all')}
                  className="ml-2 text-muted-foreground hover:text-foreground"
                >
                  ×
                </button>
              </Badge>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Announcements List */}
      <div className="space-y-4">
        {filteredAnnouncements.map((announcement) => {
          const PriorityIcon = getPriorityIcon(announcement.announcement.priority);

          return (
            <Card
              key={announcement.announcement.id}
              className={`glass-card hover-lift cursor-pointer transition-all duration-200 ${!announcement.read ? 'ring-2 ring-worldsview-blue/20' : ''
                }`}
              onClick={() => setSelectedAnnouncement(announcement)}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <Badge className={`text-white ${getPriorityColor(announcement.announcement.priority)}`}>
                        {announcement.announcement.priority.toUpperCase()}
                      </Badge>

                      <Badge variant="outline" className={`text-white ${getCategoryColor(announcement.announcement.category)}`}>
                        {announcement.announcement.category.toUpperCase()}
                      </Badge>

                      {!announcement.read && (
                        <Badge className="bg-worldsview-blue text-white">NEW</Badge>
                      )}
                    </div>

                    <div className="flex items-center gap-2 mb-2">
                      {!announcement.read && (
                        <div className="w-2 h-2 rounded-full bg-worldsview-blue flex-shrink-0" />
                      )}
                      <h3 className="flex-1 font-bold">{announcement.announcement.title}</h3>
                    </div>

                    <div className="text-muted-foreground text-sm mb-4 max-h-12 overflow-hidden whitespace-pre-wrap">
                      {announcement.announcement.content}
                    </div>

                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {announcement.announcement.publish_date}
                      </div>
                      <div>By {announcement.announcement.author}</div>
                    </div>

                    <div className="flex flex-wrap gap-1 mt-3">
                      {announcement.announcement.tags.split(',').slice(0, 3).map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs glass-badge">
                          {tag}
                        </Badge>
                      ))}
                      {announcement.announcement.tags.split(',').length > 3 && (
                        <Badge variant="outline" className="text-xs glass-badge">
                          +{announcement.announcement.tags.split(',').length - 3} more
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <Button variant="ghost" size="sm" className="glass-button">
                      <Eye className="w-4 h-4" />
                    </Button>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredAnnouncements.length === 0 && (
        <Card className="glass-card">
          <CardContent className="py-12 text-center">
            <Megaphone className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="mb-2">
              {selectedCategory === 'all' ? 'No announcements found' :
                `No ${selectedCategory.toLowerCase()} announcements found`}
            </h3>
            <p className="text-muted-foreground">
              {selectedCategory === 'all'
                ? "Try adjusting your search terms or check back later for new updates."
                : `Try selecting a different category or clearing the filter to see all announcements.`
              }
            </p>
            {selectedCategory !== 'all' && (
              <Button
                variant="outline"
                className="glass-button mt-4"
                onClick={() => setSelectedCategory('all')}
              >
                View All Announcements
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}